// KAMPÁNY-IDŐSÁV — vízszintes sávok naptári tengelyen, FIÓKONKÉNT csoportosítva.
//
// A geometria (ablak, sáv-hely, ma-vonal, csoportosítás) a `../timeline.ts` TISZTA függvényeiben él, és ott
// is van tesztelve — ez a fájl csak rajzol. Nincs külső gantt-könyvtár: a nézet egy százalék-pozicionált
// sáv-sor, amihez egy teljes library aránytalan lenne (és a design-tokeneket sem követné).
//
// MOBIL (375 px): a tengely-terület vízszintesen görgethető, a FIÓK-oszlop ragadós marad balra — különben a
// görgetés közben elveszne, melyik szálloda sávját nézzük.
import { useTranslation } from 'react-i18next';

import type { CampaignRow } from '@atlas/contracts';

import { DomainBadge } from '@/components/shared/domain-badge';
import { EmptyState } from '@/components/shared/empty-state';
import { Skeleton } from '@/components/ui/skeleton';
import { formatMoneyShort } from '@/lib/format-money';
import { cn } from '@/lib/utils';

import {
  axisTicks,
  barGeometry,
  groupByAccount,
  todayOffsetPct,
  type TimelineWindow,
  type TimelineZoom,
} from '../timeline';

export interface CampaignTimelineProps {
  rows: readonly CampaignRow[];
  window: TimelineWindow;
  zoom: TimelineZoom;
  /** A mai nap (`YYYY-MM-DD`) — paraméter, hogy a nézet tesztben determinisztikus legyen. */
  today: string;
  /**
   * Az ELSŐ betöltés fut. Enélkül az üres válasz és a még meg nem érkezett válasz ugyanúgy nézne ki:
   * a lap egy pillanatra kimondaná, hogy „nincs kampány", majd megjelennének a sávok.
   */
  isLoading?: boolean;
  onSelect: (id: string) => void;
}

/** Négy csontváz-sáv egymáshoz képest eltolva — a valódi nézet ritmusát idézi, számítás nélkül. */
const SKELETON_BARS = [
  { start: 'col-start-1', span: 'col-span-6' },
  { start: 'col-start-4', span: 'col-span-7' },
  { start: 'col-start-2', span: 'col-span-5' },
  { start: 'col-start-6', span: 'col-span-6' },
] as const;

/** A sáv színe a SZÁMÍTOTT státuszból — ugyanaz a jelentés-térkép, mint a jelmagyarázat badge-éé. */
const BAR_TONE: Record<string, string> = {
  planned: 'bg-muted-foreground/35',
  running: 'bg-primary',
  closed: 'bg-secondary',
};

export function CampaignTimeline({
  rows,
  window: win,
  zoom,
  today,
  isLoading = false,
  onSelect,
}: CampaignTimelineProps): JSX.Element {
  const { t, i18n } = useTranslation();
  const groups = groupByAccount(rows, i18n.language);
  const ticks = axisTicks(win, zoom);
  const todayPct = todayOffsetPct(win, today);

  const tickLabel = (date: string): string => {
    const d = new Date(`${date}T00:00:00.000Z`);
    if (zoom === 'month') {
      return new Intl.DateTimeFormat(i18n.language, { day: 'numeric', timeZone: 'UTC' }).format(d);
    }
    return new Intl.DateTimeFormat(i18n.language, { month: 'short', timeZone: 'UTC' }).format(d);
  };

  // A csontváz a KÉSZ nézet alakját veszi fel (fiók-oszlop + eltolt sávok), hogy a betöltés ne ugráltassa
  // a lapot. A sáv-szélességek nem számítottak — a rács-osztályok adják az eltolást.
  if (isLoading) {
    return (
      <div className="space-y-2 pt-8" aria-busy="true">
        {SKELETON_BARS.map((bar) => (
          <div key={bar.start} className="flex items-center gap-3">
            <Skeleton className="h-4 w-32 shrink-0 sm:w-44" />
            <div className="grid flex-1 grid-cols-12">
              <Skeleton className={cn('h-7', bar.start, bar.span)} />
            </div>
          </div>
        ))}
      </div>
    );
  }

  if (groups.length === 0) {
    return (
      <EmptyState
        title={t('campaignForecast.timelineEmpty')}
        description={t('campaignForecast.timelineEmptyDesc')}
        inset
      />
    );
  }

  return (
    <div className="overflow-x-auto">
      <div className="min-w-[36rem]">
        {/* ── tengely ── */}
        <div className="flex items-end">
          <div className="w-32 shrink-0 sm:w-44" />
          <div className="border-border relative h-6 flex-1 border-b">
            {ticks.map((tick) => (
              <div
                key={tick.date}
                className="absolute bottom-0 top-0 flex items-end"
                style={{ left: `${String(tick.offsetPct)}%` }}
              >
                <span
                  className={cn(
                    'text-2xs -translate-x-1/2 pb-1 tracking-[0.07em]',
                    tick.major ? 'text-foreground' : 'text-subtle',
                  )}
                >
                  {tickLabel(tick.date)}
                </span>
              </div>
            ))}
          </div>
        </div>

        {/* ── sávok fiókonként ── */}
        <div className="relative">
          {todayPct !== null && (
            <div
              className="bg-destructive/70 pointer-events-none absolute bottom-0 top-0 z-10 w-px"
              style={{ left: `calc(8rem + (100% - 8rem) * ${String(todayPct / 100)})` }}
              aria-hidden
            />
          )}
          {groups.map((group) => (
            <div key={group.accountId} className="border-border/60 border-b py-2 last:border-b-0">
              <div className="flex items-start">
                <div className="w-32 shrink-0 pr-2 sm:w-44">
                  <p className="text-md truncate font-medium">
                    {group.accountName ?? t('common.deletedAccount')}
                  </p>
                </div>
                <div className="flex-1 space-y-1">
                  {group.rows.map((row) => {
                    const bar = barGeometry(row.startsOn, row.endsOn, win);
                    if (bar === null) return null;
                    return (
                      <div key={row.id} className="relative h-7">
                        <button
                          type="button"
                          onClick={() => {
                            onSelect(row.id);
                          }}
                          title={`${row.name} · ${row.startsOn} – ${row.endsOn}`}
                          className={cn(
                            'absolute top-0 flex h-7 items-center overflow-hidden rounded-md px-2 text-left',
                            'focus-visible:ring-ring transition-colors duration-150 hover:opacity-90 focus-visible:ring-2',
                            BAR_TONE[row.status] ?? 'bg-secondary',
                            bar.clippedStart && 'rounded-l-none',
                            bar.clippedEnd && 'rounded-r-none',
                          )}
                          style={{
                            left: `${String(bar.leftPct)}%`,
                            width: `${String(bar.widthPct)}%`,
                          }}
                        >
                          <span className="text-primary-foreground truncate text-xs font-medium">
                            {row.name}
                            {row.plannedBudget !== null && (
                              <span className="pl-2 font-normal opacity-80">
                                {formatMoneyShort(row.plannedBudget, row.currency, i18n.language)}
                              </span>
                            )}
                          </span>
                        </button>
                      </div>
                    );
                  })}
                </div>
              </div>
            </div>
          ))}
        </div>

        {/* ── jelmagyarázat ── */}
        <div className="flex flex-wrap items-center gap-3 pt-3">
          {(['planned', 'running', 'closed'] as const).map((status) => (
            <span key={status} className="flex items-center gap-1.5">
              <span className={cn('h-2.5 w-2.5 rounded-sm', BAR_TONE[status])} aria-hidden />
              <DomainBadge
                kind="campaignStatus"
                value={status}
                label={t(`campaignForecast.status.${status}`)}
              />
            </span>
          ))}
        </div>
      </div>
    </div>
  );
}
